import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Bot, ExternalLink, BookOpen, Globe, FileText, ArrowRight, CheckCircle2, MessageSquare } from "lucide-react";

/**
 * Super-admin platform settings.
 * - Pick the public landing agent that powers the hellosir.ai chat bubble.
 * - Provides a clear, one-screen path to train that agent (deep links + quick
 *   URL ingestion right from this page).
 */
export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [agents, setAgents] = useState([]);
  const [picked, setPicked] = useState("");
  const [saving, setSaving] = useState(false);

  // Quick URL training
  const [trainUrl, setTrainUrl] = useState("");
  const [crawlSite, setCrawlSite] = useState(true);
  const [training, setTraining] = useState(false);

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/admin/system-settings").then((r) => r.data),
      api.get("/admin/agents").then((r) => r.data),
    ]);
    setSettings(s);
    setAgents(a);
    setPicked(s.public_landing_agent_id || "");
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/system-settings", { public_landing_agent_id: picked || null });
      await load();
      toast.success("Landing agent updated — the bubble on hellosir.ai will use this agent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const trainNow = async () => {
    if (!trainUrl.trim() || !settings?.public_landing_agent?.id) return;
    setTraining(true);
    try {
      await api.post("/knowledge/url", {
        agent_id: settings.public_landing_agent.id,
        url: trainUrl.trim(),
        crawl_site: crawlSite,
        max_pages: 12,
      });
      toast.success(crawlSite ? "Crawling started — give it a minute, then chat to test." : "URL ingested. Try chatting now.");
      setTrainUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not train agent");
    } finally {
      setTraining(false);
    }
  };

  if (!settings) return <div className="text-sm text-[#4B5563]">Loading…</div>;

  const wired = settings.public_landing_agent;
  const credits = settings.public_landing_tenant;
  const noCredits = wired && credits && (credits.ai_credits || 0) <= 0;
  const builderPath = wired ? `/dashboard/agents/${wired.id}` : null;

  return (
    <div data-testid="admin-settings-page" className="space-y-8 max-w-4xl">
      <div>
        <h1 className="font-display font-extrabold text-3xl">Platform settings</h1>
        <p className="text-sm text-[#4B5563]">Global configuration that applies to the whole platform.</p>
      </div>

      {/* OUT-OF-CREDITS BANNER */}
      {noCredits && (
        <div className="rounded-[6px] border border-[#EF4444]/30 bg-[#FEE2E2] text-[#7F1D1D] p-5 flex flex-wrap items-center gap-4" data-testid="no-credits-banner">
          <div className="font-display font-bold text-base flex-1 min-w-[260px]">
            ⚠ The bubble's agent is out of chat credits — replies are failing with "Sorry, something went wrong".
          </div>
          <Link to="/dashboard/billing" className="btn-primary text-sm inline-flex items-center gap-2">
            Top up credits <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* CURRENT BUBBLE STATUS */}
      <section className="rounded-[6px] p-6 bg-gradient-to-br from-[#F0F4FF] to-white border border-[#0044FF]/20" data-testid="landing-bubble-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-[6px] bg-[#0044FF]/12 text-[#0044FF] flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display font-bold text-xl">Live landing-page chat bubble</h2>
            <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">
              This agent powers the floating chat bubble visitors see on <span className="font-mono">hellosir.ai</span>.
              Credits are deducted from this agent's tenant.
            </p>

            {wired ? (
              <div className="mt-5 rounded-[4px] bg-white border border-[#E2E8F0] p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-[6px] flex items-center justify-center shrink-0 ${noCredits ? "bg-[#FEE2E2] text-[#EF4444]" : "bg-[#10B981]/12 text-[#10B981]"}`}>
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{wired.name}</div>
                    <div className="text-[11px] font-mono text-[#4B5563] mt-0.5">
                      Status · {wired.status || "active"}
                      {credits && (
                        <> · <span className={noCredits ? "text-[#EF4444] font-bold" : ""}>{credits.ai_credits} chat credits</span> · {credits.voice_credits} voice credits</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={builderPath}
                    className="btn-primary text-sm inline-flex items-center gap-2"
                    data-testid="train-this-agent"
                  >
                    <BookOpen className="w-4 h-4" />
                    Train this agent
                  </Link>
                  <a
                    href={`/widget/${wired.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline text-sm inline-flex items-center gap-2"
                  >
                    Preview <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-[4px] bg-[#FEF3C7] border border-[#F59E0B]/30 text-[#92400E] text-sm px-4 py-3">
                No agent selected — the bubble is currently hidden on hellosir.ai. Pick an agent below.
              </div>
            )}

            <div className="mt-6">
              <label className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">Switch landing agent</label>
              <div className="flex flex-wrap gap-2 mt-2">
                <select
                  value={picked}
                  onChange={(e) => setPicked(e.target.value)}
                  className="hs-input flex-1 min-w-[260px]"
                  data-testid="landing-agent-select"
                >
                  <option value="">— No agent (bubble hidden) —</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} · {a.type} · {a.status}
                    </option>
                  ))}
                </select>
                <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="landing-agent-save">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK TRAIN BY URL */}
      {wired && (
        <section className="rounded-[6px] p-6 bg-white border border-[#E2E8F0]" data-testid="quick-train-card">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-[6px] bg-[#10B981]/12 text-[#10B981] flex items-center justify-center shrink-0">
              <Globe className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display font-bold text-xl">Quick train — feed it a URL</h2>
              <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">
                The fastest way to teach the bubble: paste a URL we should crawl. We'll discover pages via your
                sitemap, extract the text and index it. Repeat for every section you want the agent to know.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <input
                  type="url"
                  value={trainUrl}
                  onChange={(e) => setTrainUrl(e.target.value)}
                  placeholder="https://hellosir.ai/"
                  className="hs-input flex-1 min-w-[260px]"
                  data-testid="quick-train-url"
                />
                <label className="inline-flex items-center gap-2 text-sm text-[#374151] bg-[#F9FAFB] border border-[#E2E8F0] rounded-[4px] px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={crawlSite}
                    onChange={(e) => setCrawlSite(e.target.checked)}
                    data-testid="quick-train-crawl"
                  />
                  Crawl whole site (up to 12 pages)
                </label>
                <button
                  onClick={trainNow}
                  disabled={!trainUrl.trim() || training}
                  className="btn-primary text-sm inline-flex items-center gap-2"
                  data-testid="quick-train-submit"
                >
                  {training ? "Training…" : (<>Train now <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                {["https://hellosir.ai/", "https://hellosir.ai/use-cases", "https://hellosir.ai/about", "https://hellosir.ai/contact"].map((u) => (
                  <button
                    key={u}
                    onClick={() => setTrainUrl(u)}
                    className="px-2 py-1 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] font-mono text-[#0F1115]"
                  >
                    {u.replace("https://hellosir.ai", "") || "/"}
                  </button>
                ))}
              </div>

              <p className="text-xs text-[#4B5563] mt-4">
                Need PDFs, custom text or live database integration?{" "}
                <Link to={builderPath} className="text-[#0044FF] hover:underline font-medium">
                  Open the full builder →
                </Link>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* GUIDE */}
      <section className="rounded-[6px] p-6 bg-white border border-[#E2E8F0]">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-[6px] bg-[#7C3AED]/12 text-[#7C3AED] flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display font-bold text-xl">Where to feed information to your hellosir.ai bubble</h2>
            <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">
              The bubble is just an embedded agent. Anything you teach the agent — URLs, PDFs, raw text, CSVs — instantly powers the bubble.
            </p>

            <ol className="mt-5 space-y-3 text-sm text-[#374151] leading-relaxed">
              {[
                {
                  Icon: Bot,
                  title: "Pick or create the agent",
                  body: <>Use the selector above, or <Link to="/dashboard/agents" className="text-[#0044FF] hover:underline">create a new one</Link> dedicated to your marketing site.</>,
                },
                {
                  Icon: Globe,
                  title: "Train on URLs",
                  body: <>From the agent builder, paste your homepage / about / use-case / pricing URLs and enable <em>crawl whole site</em>. We'll auto-discover sitemap and re-index on a schedule.</>,
                },
                {
                  Icon: FileText,
                  title: "Upload PDFs & docs",
                  body: <>Drag in your prospectus, pricing sheet, FAQ deck, or any policy PDF. We chunk and embed everything in seconds.</>,
                },
                {
                  Icon: Sparkles,
                  title: "Tighten the system prompt",
                  body: <>In the <em>Behaviour</em> section, set the tone, language lock, and the firm "I don't know — let me connect you" fallback so the agent never invents pricing.</>,
                },
                {
                  Icon: MessageSquare,
                  title: "Enable Chat + Voice channels",
                  body: <>Toggle on Web Chat and Web Voice. The bubble on hellosir.ai surfaces both tabs automatically.</>,
                },
                {
                  Icon: CheckCircle2,
                  title: "Top up credits",
                  body: <>Visit <Link to="/dashboard/billing" className="text-[#0044FF] hover:underline">/dashboard/billing</Link> to add chat / voice credits — they're shared across all this agent's channels.</>,
                },
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F3F4F6] text-[#0F1115] font-display font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</div>
                  <div>
                    <div className="font-display font-bold text-[#0F1115] flex items-center gap-2">
                      <step.Icon className="w-4 h-4 text-[#4B5563]" />
                      {step.title}
                    </div>
                    <div className="text-[#4B5563] mt-1">{step.body}</div>
                  </div>
                </li>
              ))}
            </ol>

            {builderPath && (
              <div className="mt-6">
                <Link to={builderPath} className="btn-primary text-sm inline-flex items-center gap-2">
                  Open the agent builder for "{wired.name}" <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
